import React, { useState, useEffect } from 'react';
import {
  withStyles,
  createStyles,
  Button,
  Typography,
  Grid,
  makeStyles,
  Theme,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton
} from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';

import { grey } from '@material-ui/core/colors';
import Switch, { SwitchClassKey, SwitchProps } from '@material-ui/core/Switch';
import EditIcon from '@material-ui/icons/Edit';
import axios, { CancelTokenSource } from 'axios';
import { GET_SETTING_UPDATE_BASE_URL } from 'constants/url';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import LoadingButtonIndicator from 'components/LoadingButtonIndicator';
import CustomizedDialog from 'components/CustomizedDialog';
import NumberFormatCustom from 'components/NumberFormatCustom';

interface Props {
  isLoading: boolean;
  reminderIdWA: number;
  remiderDayWA: number;
  reminderIsActiveWA: boolean;
  setReminderDayWA: React.Dispatch<React.SetStateAction<number>>;
  setReminderIsActiveWA: React.Dispatch<React.SetStateAction<boolean>>;
  appointmentModeId?: number;
  appointmentMode?: string;
  setAppointmentMode?: React.Dispatch<React.SetStateAction<string>>;
  handleOpenSnackbar: (type: 'success' | 'error', message: string) => void;
}

interface Styles extends Partial<Record<SwitchClassKey, string>> {
  focusVisible?: string;
}

interface IProps extends SwitchProps {
  classes: Styles;
}

const IOSSwitch = withStyles((theme: Theme) =>
  createStyles({
    root: {
      width: 42,
      height: 26,
      padding: 0,
      margin: theme.spacing(1)
    },
    switchBase: {
      padding: 1,
      '&$checked': {
        transform: 'translateX(16px)',
        color: theme.palette.common.white,
        '& + $track': {
          backgroundColor: '#53A0BE',
          opacity: 1,
          border: 'none'
        }
      },
      '&$focusVisible $thumb': {
        color: '#53A0BE',
        border: '6px solid #fff'
      }
    },
    thumb: {
      width: 24,
      height: 24
    },
    track: {
      borderRadius: 26 / 2,
      border: `1px solid ${theme.palette.grey[400]}`,
      backgroundColor: theme.palette.grey[50],
      opacity: 1,
      transition: theme.transitions.create(['background-color', 'border'])
    },
    checked: {},
    focusVisible: {}
  })
)(({ classes, ...props }: IProps) => {
  return (
    <Switch
      focusVisibleClassName={classes.focusVisible}
      disableRipple
      classes={{
        root: classes.root,
        switchBase: classes.switchBase,
        thumb: classes.thumb,
        track: classes.track,
        checked: classes.checked
      }}
      {...props}
    />
  );
});

const useStyles = makeStyles((theme: Theme) => ({
  paddingRight: {
    paddingRight: theme.spacing(1)
  },
  marginGrid: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  secondText: {
    color: grey[500],
    display: 'flex'
  }
}));

const WhatsappConfirmation: React.FC<Props> = props => {
  const classes = useStyles();

  const {
    isLoading,
    reminderIdWA,
    remiderDayWA,
    reminderIsActiveWA,
    setReminderDayWA,
    setReminderIsActiveWA,
    appointmentModeId,
    appointmentMode = 'days_before',
    setAppointmentMode,
    handleOpenSnackbar
  } = props;

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [value, setValue] = useState<number>(0);
  const [status, setStatus] = useState<boolean>(false);
  const [monthOption, setMonthOption] = useState<'first' | 'last'>('first');
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    const { reminderIsActiveWA, remiderDayWA, appointmentMode: apMode } = props;

    setValue(remiderDayWA);
    setStatus(reminderIsActiveWA);
    // derive month option from appointmentMode if provided
    if (apMode && setAppointmentMode) {
      setAppointmentMode(apMode);
      if (apMode === 'months_before_last_date_of_month') setMonthOption('last');
      else if (apMode === 'months_before_first_date_of_month') setMonthOption('first');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const handleCancelWA = () => {
    setIsEdit(!isEdit);
    setValue(remiderDayWA);
    setStatus(reminderIsActiveWA);
  };

  const handleSubmit = async () => {
    setIsProcessing(true);
    try {
      let cancelTokenSource: CancelTokenSource;
      cancelTokenSource = axios.CancelToken.source();

      const { data } = await axios.put(
        `${GET_SETTING_UPDATE_BASE_URL(reminderIdWA)}`,
        { value, isActive: isEdit ? status : !status },
        { cancelToken: cancelTokenSource.token }
      );

      setReminderDayWA(value);
      setReminderIsActiveWA(data.isActive);
      setStatus(data.isActive);
      setOpenConfirm(false);
      handleOpenSnackbar('success', 'Successfully updated setting data.');
      // also save appointment mode if provided
      if (appointmentModeId && setAppointmentMode) {
        try {
          const { data: modeData } = await axios.put(
            `${GET_SETTING_UPDATE_BASE_URL(appointmentModeId)}`,
            { value: appointmentMode },
            { cancelToken: cancelTokenSource.token }
          );
          if (modeData && modeData.value && setAppointmentMode) setAppointmentMode(modeData.value);
        } catch (err) {
          // non-fatal for mode save
          console.log('Error updating appointment mode', err);
        }
      }
    } catch (err) {
      console.log(err);
      handleOpenSnackbar('error', 'Error update setting data.');
    }
    setIsProcessing(false);
  };

  const handleChangeStatusWhatsAppReminder = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked === false) {
      setOpenConfirm(true);
    } else {
      setStatus(!status);
      handleSubmit();
    }
  };

  return (
    <>
      <Grid item xs={4}>
        <Typography variant='h5'>WhatsApp Appointment Confirmation</Typography>
        <Typography variant='body1' className={classes.secondText}>
          Settings for automatic whatsApp messages to your clients before upcoming jobs
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <FormControlLabel
          control={<IOSSwitch checked={status} onChange={handleChangeStatusWhatsAppReminder} name='status' />}
          disabled={isProcessing}
          labelPlacement='end'
          label={status ? 'Active' : 'Inactive'}
        />
      </Grid>
      <Grid item xs={6}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant='h5'>Schedule Appointment Confirmation</Typography>
            <Typography variant='body1' className={classes.secondText}>
              Choose when to send WhatsApp confirmations — either days before the appointment, or on the first or last date of the month.
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Grid container spacing={1} alignItems='center'>
              <Grid item xs={isEdit ? 12 : 9}>
                <Grid container spacing={1} alignItems='center'>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id='value'
                      label='Set Value'
                      margin='dense'
                      disabled={!isEdit}
                      value={value}
                      onChange={event => setValue(Number(event.target.value))}
                      onBlur={event => {
                        const numeric = Number(event.target.value);
                        // if months mode, allow 1..3 months, else allow 1..99 days
                        const isMonths = appointmentMode && appointmentMode.startsWith('months');
                        if (isMonths) {
                          if (numeric < 1) setValue(1);
                          else if (numeric > 3) setValue(3);
                          else setValue(numeric);
                        } else {
                          if (numeric < 1) setValue(1);
                          else if (numeric > 99) setValue(99);
                          else setValue(numeric);
                        }
                      }}
                      variant='outlined'
                      autoComplete='off'
                      InputProps={{
                        inputComponent: NumberFormatCustom as any,
                        endAdornment: (
                          <InputAdornment position='end'>
                            <TextField
                              select
                              id='appointmentMode'
                              margin='dense'
                              disabled={!isEdit}
                              // primary mode select: days_before or months_before
                              value={appointmentMode && appointmentMode.startsWith('months') ? 'months_before' : appointmentMode}
                              onChange={event => {
                                const newMode = event.target.value as string;
                                if (setAppointmentMode) {
                                  if (newMode === 'months_before') {
                                    // default to first date option when switching to months
                                    const full = monthOption === 'last' ? 'months_before_last_date_of_month' : 'months_before_first_date_of_month';
                                    setAppointmentMode(full);
                                  } else {
                                    setAppointmentMode('days_before');
                                  }
                                }
                                // adjust value limits when switching to months
                                if (newMode === 'months_before') {
                                  if (value < 0) setValue(0);
                                  if (value > 3) setValue(3);
                                } else {
                                  if (value < 1) setValue(1);
                                  if (value > 99) setValue(99);
                                }
                              }}
                              variant='standard'
                              size='small'
                              style={{ minWidth: 150, border: 'none' }}
                            >
                              <MenuItem value={'days_before'}>Day(s) Before</MenuItem>
                              <MenuItem value={'months_before'}>Month(s) Before</MenuItem>
                            </TextField>
                          </InputAdornment>
                        ),
                        inputProps: { min: appointmentMode && appointmentMode.startsWith('months') ? 0 : 1, max: appointmentMode && appointmentMode.startsWith('months') ? 3 : 99 }
                      }}
                    />
                  </Grid>
                  {/* month send option - only show when months selected */}
                  {appointmentMode && appointmentMode.startsWith('months') && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        id='monthSendOption'
                        label='Send On'
                        margin='dense'
                        disabled={!isEdit}
                        value={monthOption}
                        onChange={event => {
                          const newOpt = event.target.value as 'first' | 'last';
                          setMonthOption(newOpt);
                          if (setAppointmentMode) {
                            const full = newOpt === 'last' ? 'months_before_last_date_of_month' : 'months_before_first_date_of_month';
                            setAppointmentMode(full);
                          }
                        }}
                        variant='outlined'
                        size='small'
                      >
                        <MenuItem value={'first'}>First day of month</MenuItem>
                        <MenuItem value={'last'}>Last day of month</MenuItem>
                      </TextField>
                    </Grid>
                  )}
                  {/* <Grid item xs={8}>
                    <TextField
                      select
                      fullWidth
                      id='appointmentMode'
                      label='Mode'
                      margin='dense'
                      disabled={!isEdit}
                      value={appointmentMode}
                      onChange={event => {
                        const newMode = event.target.value as string;
                        if (setAppointmentMode) setAppointmentMode(newMode);
                        // adjust value limits when switching to months
                        if (newMode === 'months_before') {
                          if (value < 0) setValue(0);
                          if (value > 3) setValue(3);
                        } else {
                          if (value < 1) setValue(1);
                          if (value > 99) setValue(99);
                        }
                      }}
                      variant='outlined'
                    >
                      <MenuItem value={'days_before'}>Days Before</MenuItem>
                      <MenuItem value={'months_before'}>Months Before</MenuItem>
                    </TextField>
                  </Grid> */}
                </Grid>
              </Grid>
              {isEdit ? (
                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    <Grid item xs={6} className={classes.paddingRight}>
                      <Button fullWidth variant='contained' disableElevation onClick={handleCancelWA}>
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant='contained'
                        disableElevation
                        color='primary'
                        onClick={() => {
                          handleSubmit();
                          setIsEdit(!isEdit);
                        }}
                      >
                        Save
                        <LoadingButtonIndicator isLoading={isProcessing} />
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                <Grid item xs={1}>
                  <Tooltip title={'Edit'} placement='top'>
                    <IconButton size='small' disabled={!status} onClick={() => setIsEdit(!isEdit)}>
                      <EditIcon color={!status ? 'disabled' : 'primary'} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {openConfirm && (
        <CustomizedDialog
          isLoading={isProcessing}
          open={openConfirm}
          isConfirmation
          variant='warning'
          title={'Are you sure want to Inactive WhatsApp Appointment Confirmation?'}
          message=''
          secondMessage='This will affect ALL Clients, if you want to deactivate WhatsApp confirmation on specific Client, you can go to Client Profile'
          primaryButtonLabel='Ok'
          secondaryButtonLabel='Cancel'
          primaryActionButton={() => {
            handleSubmit();
            setOpenConfirm(false);
          }}
          secondaryActionButton={() => setOpenConfirm(false)}
          handleClose={() => setOpenConfirm(false)}
        />
      )}
    </>
  );
};

export default WhatsappConfirmation;
